import {
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
} from '@/entities/chart/model/constants';
import type {
  BaseFormValues,
  DashboardTemplate,
  TemplateDateTermInput,
  TemplateFormConfig,
} from '@/entities/dashboard-template';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import type {
  AddDashboard2RawParamsT,
  Dashboard2ColumnT,
} from '@/features/Dashboard/types/types';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { getOpUnitTypeByTermUnit, getTimezoneDayjs } from '@/utils/time';

import { resolveNameTemplate } from './build-chart-payloads';

/** 행 간 pos 간격 */
const ROW_POS_GAP = 1000;

/** feedback gallery 기본 기간 (최근 12개월) */
const buildDefaultDateRangeFilterSet = (): StructuredFilter => {
  const now = getTimezoneDayjs();
  return {
    [FEEDBACK_COLUMNS.DATE_START]: now.subtractVal(12, 'month').toJSON(),
    [FEEDBACK_COLUMNS.DATE_END]: now.toJSON(),
  } as StructuredFilter;
};

/** fixed/periodic dateTerm을 feedback gallery의 start_date/end_date로 변환 */
const buildDateRangeFilterSet = (
  dateTerm: TemplateDateTermInput | undefined,
): StructuredFilter => {
  if (!dateTerm) {
    return buildDefaultDateRangeFilterSet();
  }

  if (dateTerm.type === CHART_TERM_TYPE.FIXED) {
    if (!dateTerm.startDate || !dateTerm.endDate) {
      return buildDefaultDateRangeFilterSet();
    }

    return {
      [FEEDBACK_COLUMNS.DATE_START]: getTimezoneDayjs(
        dateTerm.startDate,
      ).toISOString(),
      [FEEDBACK_COLUMNS.DATE_END]: getTimezoneDayjs(
        dateTerm.endDate,
      ).toISOString(),
    } as StructuredFilter;
  }

  const now = getTimezoneDayjs();
  const value = Math.max(1, dateTerm.value);

  if (dateTerm.unit === CHART_TERM_UNIT.QUARTER) {
    const startDayOfQuarter = now.startOfQuarter();
    const previousQuarter = startDayOfQuarter.subtractQuarters(1);
    const isInclude = dateTerm.relative === CHART_PERIOD_RELATIVE.INCLUDE;

    const start = isInclude
      ? startDayOfQuarter.subtractQuarters(value - 1)
      : previousQuarter.subtractQuarters(value - 1);
    const end = isInclude
      ? startDayOfQuarter.endOfQuarter().startOf('day').addVal(1, 'day')
      : previousQuarter.endOfQuarter().startOf('day').addVal(1, 'day');

    return {
      [FEEDBACK_COLUMNS.DATE_START]: start.toJSON(),
      [FEEDBACK_COLUMNS.DATE_END]: end.toJSON(),
    } as StructuredFilter;
  }

  const unit = getOpUnitTypeByTermUnit(dateTerm.unit);
  const startDayOfUnit = now.startOf(unit);

  if (dateTerm.relative === CHART_PERIOD_RELATIVE.INCLUDE) {
    return {
      [FEEDBACK_COLUMNS.DATE_START]: startDayOfUnit
        .subtractVal(value - 1, unit)
        .toJSON(),
      [FEEDBACK_COLUMNS.DATE_END]: startDayOfUnit
        .endOf(unit)
        .startOf('day')
        .addVal(1, 'day')
        .toJSON(),
    } as StructuredFilter;
  }

  const previous = startDayOfUnit.subtractVal(1, unit);
  return {
    [FEEDBACK_COLUMNS.DATE_START]: previous
      .subtractVal(value - 1, unit)
      .toJSON(),
    [FEEDBACK_COLUMNS.DATE_END]: previous
      .endOf(unit)
      .startOf('day')
      .addVal(1, 'day')
      .toJSON(),
  } as StructuredFilter;
};

/** 여러 StructuredFilter를 하나로 합치기 */
const mergeFilters = (...filters: StructuredFilter[]): StructuredFilter => {
  const result: StructuredFilter = {};
  for (const filter of filters) {
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) {
        result[key] = value;
      }
    }
  }
  return result;
};

/** formConfig.fieldMapping을 사용해 sourceField 필터를 만든다 */
const buildFilterForSourceField = <TFormValues extends BaseFormValues>(
  sourceField: string,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): StructuredFilter => {
  const mapper = formConfig.fieldMapping[sourceField];
  return mapper ? mapper(formValues) : {};
};

const matchesSectionVariant = <TFormValues extends BaseFormValues>(
  sectionVariants: string[] | undefined,
  formValues: TFormValues,
): boolean => {
  if (!sectionVariants || sectionVariants.length === 0) {
    return true;
  }

  const sectionVariant = (formValues as { sectionVariant?: string })
    .sectionVariant;

  return sectionVariant ? sectionVariants.includes(sectionVariant) : true;
};

/**
 * 템플릿 레이아웃 + 생성된 차트 alias 목록 → addDashboardRow 파라미터 배열 변환
 *
 * 순수 함수. 호출측에서 순차 실행하여 순서를 보장한다.
 * - text 행: DASHBOARD_COLUMN_TEXT_BOX 컬럼 1개 ({{token}} 치환 포함)
 * - chart 행: chartIndices를 chartAliases로 매핑하여 DASHBOARD_COLUMN_CHART 컬럼 N개
 * - feedback-gallery 행: feedbacks_setting(title/sort/filter_set) 컬럼 1개
 * - 생성 실패한 차트(alias가 null)는 건너뛰기
 */
export const buildDashboardLayout = <TFormValues extends BaseFormValues>(
  template: DashboardTemplate<TFormValues>,
  chartAliases: (string | null)[],
  formValues: TFormValues,
): Array<AddDashboard2RawParamsT & { height?: string }> => {
  return template.layout
    .filter((row) => matchesSectionVariant(row.sectionVariants, formValues))
    .map((row, index) => {
      const pos = index * ROW_POS_GAP;

      if (row.type === 'text') {
        const resolvedText = resolveNameTemplate(row.text, formValues);
        const column: Dashboard2ColumnT<Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX> =
          {
            type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
            text: resolvedText,
          };
        return { pos, columns: [column], height: row.height };
      }

      if (row.type === 'feedback-gallery') {
        const termSourceField = row.termSourceField ?? 'dateTerm';
        const dateTerm = (formValues as Record<string, unknown>)[
          termSourceField
        ] as TemplateDateTermInput | undefined;

        const mappedSourceFilter = mergeFilters(
          ...(row.filterSourceFields ?? []).map((field) =>
            buildFilterForSourceField(field, template.formConfig, formValues),
          ),
        );

        const filterSet = {
          ...(row.filterSet ?? {}),
          ...mappedSourceFilter,
          ...buildDateRangeFilterSet(dateTerm),
        } as StructuredFilter;

        const column: Dashboard2ColumnT<Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY> =
          {
            type: Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY,
            feedbacks_setting: {
              filter_set: filterSet,
              title: resolveNameTemplate(row.title, formValues),
              sort_by: row.sortBy ?? 'date',
              order_by: row.orderBy ?? 'desc',
            },
          };
        return { pos, columns: [column], height: row.height };
      }

      if (row.type === 'chart') {
        // chart 행: 성공한 차트만 포함
        const columns: Dashboard2ColumnT<Dashboard2ColumnType.DASHBOARD_COLUMN_CHART>[] =
          row.chartIndices.reduce<
            Dashboard2ColumnT<Dashboard2ColumnType.DASHBOARD_COLUMN_CHART>[]
          >((acc, chartIndex) => {
            const alias = chartAliases[chartIndex];
            if (alias) {
              acc.push({
                type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
                chart: alias,
              });
            }
            return acc;
          }, []);

        return { pos, columns, height: row.height };
      }

      const _never: never = row;
      throw new Error(`지원하지 않는 row type입니다: ${String(_never)}`);
    });
};
